/**
 * Lens Parser Utility
 * Extracts hoverable constructs from Interactive Lens HTML
 */

export interface LensConstruct {
  id: number
  code: string
  surface: string              // Code comparison (e.g., "Python: def → JS: function")
  concept: string              // Full semantic explanation
  paradigm: string             // Paradigm/philosophy explanation
  badge: string                // Language badge (e.g., "P" for Python)
  lineNumber?: number
  conceptName: string          // Concept name/label (from data-concept-name attribute)
}

/**
 * Extract all lens constructs from generated HTML
 * @param html - The HTML string containing hoverable elements
 * @returns Array of lens constructs with their explanations
 */
export function extractLensConstructs(html: string): LensConstruct[] {
  if (!html) return []

  // Create a temporary DOM element to parse HTML
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  // Find all hoverable elements
  const hoverableElements = doc.querySelectorAll('.hoverable')

  const constructs: LensConstruct[] = []

  hoverableElements.forEach((element, index) => {
    const surface = element.getAttribute('data-surface') || ''
    const concept = element.getAttribute('data-concept') || ''
    const paradigm = element.getAttribute('data-paradigm') || ''
    const badge = element.getAttribute('data-badge') || ''
    const code = element.textContent?.trim() || ''

    // Skip if no meaningful data
    if (!surface && !concept && !paradigm) return

    // Read concept name from attribute (AI-generated), fallback to inference if missing
    const conceptName = element.getAttribute('data-concept-name') ||
                        inferConstructType(code, element)

    constructs.push({
      id: index + 1,
      code,
      surface,
      concept,
      paradigm,
      badge,
      conceptName
    })
  })

  return constructs
}

/**
 * Infer the type of construct based on code content and HTML class
 * @param code - The code snippet
 * @param element - The DOM element
 * @returns Human-readable construct type
 */
function inferConstructType(code: string, element: Element): string {
  const className = element.className || ''

  // Check HTML class first
  if (className.includes('keyword')) {
    // Common keywords
    if (/^(function|def|fn|func)$/i.test(code)) return 'Function Declaration'
    if (/^(class|struct|interface|trait)$/i.test(code)) return 'Type Definition'
    if (/^(let|var|const|mut)$/i.test(code)) return 'Variable Declaration'
    if (/^(if|else|match|switch|case)$/i.test(code)) return 'Control Flow'
    if (/^(for|while|loop|forEach)$/i.test(code)) return 'Loop Construct'
    if (/^(async|await|promise)$/i.test(code)) return 'Async Pattern'
    if (/^(import|export|require|use|include)$/i.test(code)) return 'Module System'
    if (/^(new|malloc|free|Box|Rc)$/i.test(code)) return 'Memory Management'
    if (/^(return|yield|throw)$/i.test(code)) return 'Flow Control'
    return 'Keyword'
  }

  if (className.includes('type-keyword')) return 'Type Annotation'
  if (className.includes('function')) return 'Function Call'
  if (className.includes('operator')) return 'Operator'

  // Fallback: try to infer from code content
  if (/^\w+\s*\(/.test(code)) return 'Function Call'
  if (/^[A-Z]\w+$/.test(code)) return 'Type Reference'
  if (/^(true|false|null|undefined|None)$/i.test(code)) return 'Literal Value'

  return 'Code Element'
}

/**
 * Generate a mini code diff for a construct
 * Extracts source/target equivalents from the surface layer
 * @param surface - The surface layer explanation
 * @returns Object with source and target code, plus flags for "(none)" markers
 */
export function parseSurfaceDiff(surface: string): {
  source: string;
  target: string;
  sourceIsNone?: boolean;
  targetIsNone?: boolean;
} {
  if (!surface) return { source: '', target: '' }

  // Support multiple formats:
  // 1. "In Python: def → In JavaScript: function"
  // 2. "Python's def → JavaScript's function"
  // 3. "C++: int x → Python: x" (V1 format with language prefixes)
  // 4. "malloc() → Box::new()" (simple code comparison)
  // 5. "(none) → code" or "code → (none)" for no equivalents

  const patterns = [
    /In\s+(\w+):\s*(.+?)\s*→\s*In\s+(\w+):\s*(.+)/i,
    /(\w+)'s\s+(.+?)\s*→\s*(\w+)'s\s+(.+)/i,
    /([A-Za-z][A-Za-z0-9+#]*?):\s*(.+?)\s*→\s*([A-Za-z][A-Za-z0-9+#]*?):\s*(.+)/,  // "C++: code → Python: code"
    /^(.+?)\s*→\s*(.+)$/  // Simple arrow split
  ]

  for (const pattern of patterns) {
    const match = surface.match(pattern)
    if (match) {
      let source: string
      let target: string

      if (pattern.source.includes('In\\s+')) {
        // "In Python: def → In JavaScript: function" - strip "In Python:" prefix
        source = match[2].trim()
        target = match[4].trim()
      } else if (pattern.source.includes("'s")) {
        // "Python's def → JavaScript's function" - strip "Python's" prefix
        source = match[2].trim()
        target = match[4].trim()
      } else if (pattern.source.includes('A-Za-z0-9+#')) {
        // "C++: int x → Python: x" - strip language prefixes
        source = match[2].trim()
        target = match[4].trim()
      } else {
        // Simple split on arrow
        source = match[1].trim()
        target = match[2].trim()
      }

      // Detect "(none)" or "(no equivalent)" markers
      const sourceIsNone = /^\(none\)$/i.test(source) || /^\(no equivalent\)$/i.test(source)
      const targetIsNone = /^\(none\)$/i.test(target) || /^\(no equivalent\)$/i.test(target)

      return {
        source: sourceIsNone ? '(none)' : source,
        target: targetIsNone ? '(none)' : target,
        sourceIsNone,
        targetIsNone
      }
    }
  }

  // Fallback: if no arrow, might be descriptive
  return { source: '', target: surface }
}

/**
 * Group constructs by type for better organization
 * @param constructs - Array of lens constructs
 * @returns Constructs grouped by type
 */
export function groupConstructsByType(constructs: LensConstruct[]): Record<string, LensConstruct[]> {
  return constructs.reduce((groups, construct) => {
    const type = construct.conceptName
    if (!groups[type]) {
      groups[type] = []
    }
    groups[type].push(construct)
    return groups
  }, {} as Record<string, LensConstruct[]>)
}

/**
 * Rebuild HTML from constructs after regeneration
 * Takes the original HTML and updated constructs, and produces new HTML with regenerated insights
 * @param originalHTML - The original lens HTML
 * @param updatedConstructs - Array of constructs with potentially regenerated insights
 * @returns New HTML with updated data attributes
 */
export function rebuildHTMLFromConstructs(originalHTML: string, updatedConstructs: LensConstruct[]): string {
  if (!originalHTML || updatedConstructs.length === 0) return originalHTML

  // Create a temporary DOM element to parse HTML
  const parser = new DOMParser()
  const doc = parser.parseFromString(originalHTML, 'text/html')

  // Find all hoverable elements
  const hoverableElements = doc.querySelectorAll('.hoverable')

  // Update each hoverable element with corresponding construct data
  hoverableElements.forEach((element, index) => {
    const construct = updatedConstructs[index]
    if (!construct) return // Safety check

    // Update data attributes with (potentially) regenerated insights
    element.setAttribute('data-surface', construct.surface)
    element.setAttribute('data-concept', construct.concept)
    element.setAttribute('data-paradigm', construct.paradigm)
    if (construct.badge) {
      element.setAttribute('data-badge', construct.badge)
    }
    if (construct.conceptName) {
      element.setAttribute('data-concept-name', construct.conceptName)
    }
  })

  // Return the updated HTML
  return doc.documentElement.outerHTML
}
